'use client'
import { Fragment, useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import {
  ArrowLeft,
  ChevronRight,
  CircleUser,
  ClipboardCheck,
  Download,
  LogOut,
  MapPin,
  Phone,
  ShoppingCart,
  Users,
} from 'lucide-react'
import '../../assets/css/navbar.css'
import { NavbarHamburgerIcon } from '../../helper/SVG'
import ActionButton from './ActionButton'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { Button } from '../ui/button'
import { Avatar, AvatarFallback } from '../ui/avatar'
import { usePathname, useRouter } from 'next/navigation'
import useCustomToast from '@/hooks/useCustomToast'
import { dispatch, useSelector } from '@/lib/store'
import { FE_AUTH_LOGOUT_API } from '@/helper/apiConstants'
import { updateLoginData, resetLoginData } from '@/lib/reducers/loginReducer'
import MWebLocationAndSearchBar from './MWebLocationAndSearchBar'
import { resetCartData, updateCartData } from '@/lib/reducers/cartReducer'

import CART_HELPER from '@/helper/cartHelper'
import PATIENT_HELPER from '@/helper/patientHelper'
import { updateOrderData, resetOrderData } from '@/lib/reducers/orderReducer'
import { resetUserProfile } from '@/lib/reducers/userReducer'
import {
  CHECKOUT_PAGE,
  HOME_PAGE,
  LEAD_PAGE,
  MANAGE_MEMBERS_PAGE,
  MY_ADDRESS_PAGE,
  MY_BOOKINGS_PAGE,
  MY_REPORTS_PAGE,
  ORDER_CONFIRMATION_PAGE,
  CURRENT_SUBSIDIARY,
  DDRC_LEAD_PAGE,
  NEARBY_LABS_PAGE,
  LAB_DETAILS_PAGE,
  AGILUS_COINS_PAGE,
  AGILUS_SUBSIDIARY,
  PROFILE_PAGES,
  CONSUMER_WEB_PLATFORM,
  SUBSIDIARY_CONFIG,
  DEFAULT_CITY_OBJ,
  DDRC_SUBSIDIARY,
  DEFAULT_CITY_UUID,
  EDOS_PAGE,
} from '@/utils/constants'
import {
  isEmpty,
  isNonCityRoute,
  getLeadPageCitySlug,
} from '@/utils/commonFunc'
import { updateCityData } from '@/lib/reducers/cityReducer'
import LOCATION_HELPER from '@/helper/locationHelper'
import { ChevronDown } from 'lucide-react'
import MobileDrawer from './MobileDrawer'
import SecondaryNav from './SecondaryNav'
import AgilusLogo from '@/assets/icons/AgilusLogo'
import AgilusPhadkeLogo from '@/assets/icons/AgilusPhadkeLogo'
import AgilusDDRCLogo from '@/assets/icons/AgilusDDRCLogo'
import NABLLogo from '@/assets/images/nablLogo.png'
import CAPLogo from '@/assets/images/capLogo.png'
import Image from 'next/image'
import {
  loadState,
  saveState,
  deleteLocalStorageAndCookies,
} from '@/utils/localStorage'
import { eventConstants, getMoengage, trackEvent } from '@/utils/events'
import LOGO_HELPER from '@/helper/logoHelper'
import { AgilusCoinsIcon } from '@/helper/loyaltySvg'
import AgilusCoinsIconSized from '@/assets/icons/AgilusCoinsIcon'
import HeaderSearchBar from './HeaderSearchBar'
import type { SharedCatalog } from '@/helper/serverFetchers/sharedCatalog'

const Navbar = ({
  sharedCatalog,
}: {
  sharedCatalog?: SharedCatalog | null
}) => {
  const { CUSTOMER_CARE_NUMBER, CUSTOMER_CARE_NUMBER_WITH_TEL } =
    SUBSIDIARY_CONFIG
  const showToast = useCustomToast()
  const pathname = usePathname()
  // Lead/campaign landing pages (/agiluscare, /lp/agiluscare/..., DDRC packages) hide the full nav for a minimal header.
  const isLeadPage =
    !!pathname?.includes(LEAD_PAGE) || pathname === DDRC_LEAD_PAGE
  const router = useRouter()

  const userState = useSelector((state) => state.user) || {}
  const loginState = useSelector((state) => state?.login) || {}
  const cartState = useSelector((state) => state?.cart) || {}
  const cityState = useSelector((state) => state.city) || {}

  const [cityFromQuery, setCityFromQuery] = useState<string | null>(null)

  const currentSelectedCity = cityState?.currentCityObj

  const leadPageCitySlug = getLeadPageCitySlug(pathname)
  const leadPageCityObj: any = leadPageCitySlug
    ? cityState?.allCitiesList?.find(
        (item: any) => item?.slugName?.toLowerCase() === leadPageCitySlug
      )
    : null
  const leadPageSubsidiary = leadPageCityObj?.subsidiary || CURRENT_SUBSIDIARY

  const cityLocalStorageData = loadState('city') || {}
  const loggedInMobileNumber = loadState('ctxVal') || null

  // Pages where city selector and searchbar should be hidden
  const isCheckoutOrConfirmationPage =
    pathname === CHECKOUT_PAGE || pathname === ORDER_CONFIRMATION_PAGE
  const isProfilePage = PROFILE_PAGES.includes(pathname)
  const isEdosPage = pathname.includes(EDOS_PAGE)
  const showCityOrSearchbar =
    !isCheckoutOrConfirmationPage &&
    pathname !== NEARBY_LABS_PAGE &&
    !pathname.includes(LAB_DETAILS_PAGE)
  const showSearchBar = showCityOrSearchbar && !isEdosPage

  const { handleGetCartItems, handleRestoreCartItems } = CART_HELPER()
  const { handleGetUserDetails } = PATIENT_HELPER()
  const { handleGetCityListData } = LOCATION_HELPER()
  const { handleSubsidiaryLogo } = LOGO_HELPER()

  const hasDispatched = useRef(false)

  const [navIsOpen, setNavIsOpen] = useState(false)
  const [locationModalIsOpen, setLocationModalIsOpen] = useState(false)
  const [isCityListFetched, setIsCityListFetched] = useState(false)
  const [isMounted, setIsMounted] = useState(false)
  const [profileSheetOpen, setProfileSheetOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)

  // Prevent hydration mismatch by only rendering auth-dependent UI after mount
  useEffect(() => {
    setIsMounted(true)
  }, [])

  // Add shadow on scroll
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const CART_ITEMS = cartState?.cartDtoList || []
  const IS_USER_LOGGED_IN = loginState?.isUserLoggedIn || false

  const collectiveCartItems = CART_ITEMS?.reduce((total: any, item: any) => {
    return total + item?.productDtoList?.length
  }, 0)

  const handleRedirect = (e: any) => {
    e.preventDefault()
    const eventPayload = {
      u_mb: loggedInMobileNumber || '',
      city: cityState?.currentCityObj?.name || null,
      app: CONSUMER_WEB_PLATFORM,
      subsidiary: CURRENT_SUBSIDIARY,
    }
    trackEvent(eventConstants.CALL_CUSTOMER_CARE, eventPayload)
    if (typeof window !== undefined) {
      window.location.href = CUSTOMER_CARE_NUMBER_WITH_TEL
    }
  }

  const handleUserLogout = async () => {
    let apiResponse: any = null
    try {
      const res = await fetch(FE_AUTH_LOGOUT_API, {
        method: 'POST',
        headers: { abcAxis: loadState('abcAxis') || '' },
      })
      apiResponse = await res.json()
    } catch (e) {
      console.error('error', e)
    }

    // logout call must not block the client from clearing its own state.
    if (CURRENT_SUBSIDIARY !== DDRC_SUBSIDIARY) {
      const Moengage = getMoengage()
      if (Moengage) await Moengage.destroy_session()
    }
    dispatch(resetUserProfile())
    dispatch(resetLoginData())
    dispatch(resetCartData())
    dispatch(resetOrderData())
    await deleteLocalStorageAndCookies()
    showToast(
      apiResponse?.status === true ? 'success' : 'danger',
      apiResponse?.message || 'Logged out'
    )
    router.push(HOME_PAGE)
  }

  const handleGetCityListing = async () => {
    const { sortedCityArr } = await handleGetCityListData()

    if (isNonCityRoute(pathname)) {
      dispatch(
        updateCityData({
          ...cityState,
          currentCityObj: DEFAULT_CITY_OBJ || {},
          allCitiesList: [...sortedCityArr],
        })
      )
      if (DEFAULT_CITY_OBJ) saveState('city', DEFAULT_CITY_OBJ)
      setIsCityListFetched(true)
      return
    }

    // Read localStorage fresh to avoid stale closure
    const freshCityData = loadState('city') || {}

    const hasFreshCity =
      Object.keys(freshCityData)?.length > 0 &&
      Object.values(freshCityData).every((item) => item !== '')

    const cityToSet = hasFreshCity
      ? freshCityData
      : { ...cityState?.currentCityObj }

    dispatch(
      updateCityData({
        ...cityState,
        currentCityObj: cityToSet,
        allCitiesList: [...sortedCityArr],
      })
    )
    setIsCityListFetched(true)
  }

  useEffect(() => {
    const fetchCities = async () => {
      if (cityState?.allCitiesList?.length > 0) {
        setIsCityListFetched(true)
        return
      }
      await handleGetCityListing()
    }

    fetchCities()
  }, [cityState?.allCitiesList?.length])

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const city = urlParams.get('city')
    setCityFromQuery(city)

    if (city && cityState?.currentCityObj?.slugName !== city) {
      const selectedCityFromUrl = cityState.allCitiesList.find(
        (item: any) => item?.slugName === cityFromQuery
      )

      if (selectedCityFromUrl) {
        dispatch(
          updateCityData({ ...cityState, currentCityObj: selectedCityFromUrl })
        )
        saveState('city', selectedCityFromUrl)

        hasDispatched.current = true
        return
      }
    }

    if (isNonCityRoute(pathname)) {
      // Non-city product routes: set default city
      if (DEFAULT_CITY_OBJ) {
        dispatch(
          updateCityData({ ...cityState, currentCityObj: DEFAULT_CITY_OBJ })
        )
        saveState('city', DEFAULT_CITY_OBJ)
        hasDispatched.current = true
      }
    } else if (!hasDispatched.current) {
      if (
        cityLocalStorageData &&
        Object.keys(cityLocalStorageData)?.length > 0 &&
        Object.values(cityLocalStorageData).every((item) => item !== '')
      ) {
        dispatch(
          updateCityData({
            ...cityState,
            currentCityObj: cityLocalStorageData,
            isCityServicable: true,
          })
        )
        hasDispatched.current = true
      }
    }
  }, [cityState?.allCitiesList, pathname, dispatch])

  useEffect(() => {
    if (!loggedInMobileNumber || !isCityListFetched) return

    const fetchDataAndUpdateStore = async () => {
      await handleGetUserDetails()
      if (!isEmpty(cityState?.allCitiesList)) {
        const apiRes: any = await handleGetCartItems()
        if (apiRes?.status) {
          dispatch(updateCartData({ ...cartState, ...apiRes.data }))
          dispatch(
            updateOrderData({
              cartDtoList: apiRes.data.cartDtoList || [],
            })
          )

          // Restore cart with default city pricing on non-city routes
          if (isNonCityRoute(pathname)) {
            if (
              DEFAULT_CITY_UUID &&
              apiRes.data?.cartDtoList?.[0]?.productDtoList?.length > 0 &&
              apiRes.data?.cityUuid !== DEFAULT_CITY_UUID
            ) {
              const restoreRes: any = await handleRestoreCartItems(
                userState?.phoneNumber,
                DEFAULT_CITY_UUID
              )
              if (restoreRes?.status === true) {
                dispatch(
                  updateCartData({
                    ...cartState,
                    cartDtoList: restoreRes.data?.cartDtoList,
                    cityUuid: restoreRes.data?.cityUuid,
                    totalAmount: restoreRes.data?.totalAmount,
                  })
                )
              }
            }
          }
        }
      }
    }

    fetchDataAndUpdateStore()
  }, [loggedInMobileNumber, isCityListFetched, pathname])

  return (
    <Fragment>
      {pathname === DDRC_LEAD_PAGE && (
        <header className="py-3 border-b border-solid border-gray-100 bg-white-100">
          <div className="container max-w-full flex gap-4 items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-10">
              <div className="flex items-center gap-12">
                <Link title="home" href="#">
                  {handleSubsidiaryLogo(leadPageSubsidiary)}
                </Link>
              </div>
            </div>
            <div className="flex gap-0 sm:gap-14 items-end h-11">
              <div className="max-h-11 h-full ">
                <Image
                  src={NABLLogo}
                  alt="NABL LOGO"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="max-h-11 h-full ">
                <Image
                  src={CAPLogo}
                  alt="CAP LOGO"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </div>
        </header>
      )}
      {!isLeadPage && (
        <Fragment>
          <header
            className={`border-b border-solid border-gray-100 bg-white navbar transition-shadow duration-300 ${
              isScrolled ? 'shadow-md' : ''
            } ${isProfilePage ? 'hidden lg:block' : ''}`}
          >
            <div className="container max-w-full flex flex-col h-full">
              {/* Row 1: Hamburger, Logo, right-side actions */}
              <div className="flex items-center justify-between w-full py-2 lg:py-0 gap-4 lg:h-full">
                <div className="flex gap-2 items-center flex-shrink-0">
                  <button
                    className="navbar-toggler"
                    onClick={() => setNavIsOpen((prev) => !prev)}
                  >
                    <NavbarHamburgerIcon />
                  </button>
                  <Link title="home" href={HOME_PAGE} className="flex-shrink-0">
                    {handleSubsidiaryLogo(
                      cityState?.currentCityObj?.subsidiary
                    )}
                  </Link>
                  {/* City selector - desktop only (inline with logo) */}
                  {showCityOrSearchbar && (
                    <div className="hidden lg:flex items-center gap-4">
                      <div className="h-6 w-px bg-gray-300 flex-shrink-0" />
                      {!isMounted || !isCityListFetched ? (
                        <div className="flex items-center gap-1.5">
                          <div className="w-4 h-4 bg-gray-200 animate-pulse rounded-full flex-shrink-0" />
                          <div className="w-[80px] h-4 bg-gray-200 animate-pulse rounded" />
                        </div>
                      ) : (
                        <button
                          className="flex items-center gap-1.5 hover:opacity-80 transition-opacity"
                          onClick={() => setLocationModalIsOpen(true)}
                        >
                          <MapPin
                            size={16}
                            className="flex-shrink-0 text-primary-500"
                          />
                          <div className="flex items-center gap-0.5">
                            <span className="text-body-sm font-semibold text-gray-800 capitalize">
                              {currentSelectedCity?.name?.toLowerCase() || ''}
                            </span>
                            <ChevronDown
                              size={14}
                              className="flex-shrink-0 text-gray-800"
                            />
                          </div>
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {/* Desktop search bar */}
                {showSearchBar && <HeaderSearchBar />}
                <div className="flex items-center gap-3 sm:gap-5 justify-end flex-shrink-0">
                  <div className="call-text text-nowrap">
                    <Link
                      href={CUSTOMER_CARE_NUMBER_WITH_TEL}
                      target="_blank"
                      onClick={handleRedirect}
                      className="inline-flex items-center gap-1.5 px-3 h-9 bg-primary-50 hover:bg-primary-100 border border-primary-200/60 rounded-full transition-colors duration-200 group"
                    >
                      <Phone size={14} className="text-primary-600" />
                      <span className="text-label-sm font-semibold text-primary-700 group-hover:text-primary-800">
                        Home Collection
                      </span>
                      <span className="text-label-sm font-semibold text-primary-700 group-hover:text-primary-800">
                        {CUSTOMER_CARE_NUMBER}
                      </span>
                    </Link>
                  </div>
                  {/* Auth-dependent elements wrapper */}
                  <div className="flex items-center gap-3 sm:gap-5 justify-end">
                    {/* Cart Icon */}
                    {isMounted &&
                      !!IS_USER_LOGGED_IN &&
                      !isCheckoutOrConfirmationPage && (
                        <button
                          className="relative"
                          onClick={() =>
                            isMounted &&
                            IS_USER_LOGGED_IN &&
                            dispatch(
                              updateCartData({
                                ...cartState,
                                modalIsOpen: true,
                              })
                            )
                          }
                        >
                          <ShoppingCart size={24} className="text-gray-600" />
                          {collectiveCartItems > 0 && (
                            <div className="bg-primary-600 rounded-full grid place-content-center absolute -right-2 -top-2 w-5 h-5">
                              <p className="text-[10px] leading-none font-semibold text-white-100">
                                {collectiveCartItems}
                              </p>
                            </div>
                          )}
                        </button>
                      )}
                    {/* Agilus Coins + Profile Dropdown */}
                    {!isCheckoutOrConfirmationPage && (
                      <div className="flex items-center justify-center">
                        {!isMounted ? (
                          <>
                            {/* Placeholder to reserve chip space and prevent layout shift */}
                            <div className="flex items-center gap-2 pl-1.5 pr-3.5 h-10 bg-amber-50/50 border border-amber-100 rounded-full mr-3 animate-pulse">
                              <div className="w-6 h-6 bg-amber-100 rounded-full" />
                              <div className="w-6 h-3.5 bg-amber-100 rounded" />
                            </div>
                            <CircleUser size={24} className="text-gray-600" />
                          </>
                        ) : loggedInMobileNumber ? (
                          <>
                            {/* Agilus Coins Chip */}
                            <button
                              className="block mr-3"
                              onClick={() => {
                                setProfileSheetOpen(false)
                                router.push(AGILUS_COINS_PAGE)
                              }}
                            >
                              {userState?.availablePoints == null ? (
                                <div className="flex items-center gap-2 pl-1.5 pr-3.5 h-10 bg-amber-50/50 border border-amber-100 rounded-full animate-pulse">
                                  <div className="w-6 h-6 bg-amber-100 rounded-full" />
                                  <div className="w-6 h-3.5 bg-amber-100 rounded" />
                                </div>
                              ) : (
                                <div className="relative flex items-center gap-1.5 sm:gap-2 pl-1 sm:pl-1.5 pr-2.5 sm:pr-3.5 h-8 sm:h-9 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200/60 rounded-full hover:shadow-[0_0_12px_rgba(251,191,36,0.3)] hover:border-amber-300 hover:scale-[1.02] transition-all duration-300 cursor-pointer overflow-hidden group">
                                  {/* Shimmer overlay */}
                                  <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full bg-gradient-to-r from-transparent via-white/40 to-transparent transition-transform duration-700 ease-in-out" />
                                  <div className="coins-chip-icon flex items-center justify-center relative z-10">
                                    <AgilusCoinsIconSized size={24} />
                                  </div>
                                  <span className="text-body-sm font-semibold text-gray-900 tabular-nums relative z-10">
                                    {userState?.availablePoints}
                                  </span>
                                </div>
                              )}
                            </button>
                            <Sheet
                              open={profileSheetOpen}
                              onOpenChange={setProfileSheetOpen}
                            >
                              {/* Profile Icon */}
                              <SheetTrigger className="focus:outline-none focus:ring-0">
                                <CircleUser
                                  size={24}
                                  className="text-gray-600"
                                />
                              </SheetTrigger>
                              <SheetContent
                                side="right"
                                hideClose
                                className="w-full sm:w-[380px] p-0 flex flex-col bg-gray-100 gap-0"
                              >
                                {/* Header */}
                                <SheetHeader className="px-4 py-4 bg-white border-b border-gray-200 flex-row items-center gap-3 space-y-0">
                                  <button
                                    onClick={() => setProfileSheetOpen(false)}
                                    className="p-1 -ml-1 hover:bg-gray-100 rounded-full transition-colors"
                                  >
                                    <ArrowLeft size={20} />
                                  </button>
                                  <SheetTitle className="text-heading-sm text-gray-900">
                                    Profile
                                  </SheetTitle>
                                </SheetHeader>

                                {/* Profile Info Section */}
                                <div className="p-5 bg-gray-100">
                                  <div className="flex items-center gap-4">
                                    <Avatar className="h-20 w-20">
                                      <AvatarFallback className="bg-primary-200 text-primary-700 text-heading-lg font-semibold">
                                        {userState?.firstName
                                          ?.slice(0, 1)
                                          ?.toUpperCase()}
                                      </AvatarFallback>
                                    </Avatar>
                                    <div className="flex-1 min-w-0">
                                      <h3 className="text-heading-sm text-gray-900">
                                        {userState?.firstName?.toLowerCase()}
                                      </h3>
                                      <p className="text-body-sm text-gray-700 mt-0.5">
                                        {loggedInMobileNumber
                                          ? `+91 ${loggedInMobileNumber}`
                                          : ''}
                                      </p>
                                      {userState?.code && (
                                        <p className="text-body-xs text-gray-700 mt-0.5">
                                          Patient ID: {userState?.code}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {/* Menu Items */}
                                <div className="px-4">
                                  <div className="bg-white rounded-xl overflow-hidden">
                                    <Button
                                      className="w-full bg-transparent px-4 py-4 text-gray-800 shadow-none border-none rounded-none flex justify-between items-center h-auto"
                                      onClick={() => {
                                        setProfileSheetOpen(false)
                                        router.push(MY_BOOKINGS_PAGE)
                                      }}
                                    >
                                      <div className="flex items-center gap-3">
                                        <ClipboardCheck
                                          size={20}
                                          className="text-gray-800"
                                        />
                                        <span className="text-body-sm font-medium">
                                          My Bookings
                                        </span>
                                      </div>
                                      <ChevronRight
                                        size={20}
                                        className="text-gray-400"
                                      />
                                    </Button>
                                    <div className="mx-4 h-px bg-gray-200" />
                                    <Button
                                      className="w-full bg-transparent px-4 py-4 text-gray-800 shadow-none border-none rounded-none flex justify-between items-center h-auto"
                                      onClick={() => {
                                        setProfileSheetOpen(false)
                                        router.push(MY_ADDRESS_PAGE)
                                      }}
                                    >
                                      <div className="flex items-center gap-3">
                                        <MapPin
                                          size={20}
                                          className="text-gray-800"
                                        />
                                        <span className="text-body-sm font-medium">
                                          My Address
                                        </span>
                                      </div>
                                      <ChevronRight
                                        size={20}
                                        className="text-gray-400"
                                      />
                                    </Button>
                                    <div className="mx-4 h-px bg-gray-200" />
                                    <Button
                                      className="w-full bg-transparent px-4 py-4 text-gray-800 shadow-none border-none rounded-none flex justify-between items-center h-auto"
                                      onClick={() => {
                                        setProfileSheetOpen(false)
                                        router.push(MANAGE_MEMBERS_PAGE)
                                      }}
                                    >
                                      <div className="flex items-center gap-3">
                                        <Users
                                          size={20}
                                          className="text-gray-800"
                                        />
                                        <span className="text-body-sm font-medium">
                                          Manage Members
                                        </span>
                                      </div>
                                      <ChevronRight
                                        size={20}
                                        className="text-gray-400"
                                      />
                                    </Button>
                                    <div className="mx-4 h-px bg-gray-200" />
                                    <Button
                                      className="w-full bg-transparent px-4 py-4 text-gray-800 shadow-none border-none rounded-none flex justify-between items-center h-auto"
                                      onClick={() => {
                                        setProfileSheetOpen(false)
                                        router.push(MY_REPORTS_PAGE)
                                      }}
                                    >
                                      <div className="flex items-center gap-3">
                                        <Download
                                          size={20}
                                          className="text-gray-800"
                                        />
                                        <span className="text-body-sm font-medium">
                                          My Reports
                                        </span>
                                      </div>
                                      <ChevronRight
                                        size={20}
                                        className="text-gray-400"
                                      />
                                    </Button>
                                    <div className="mx-4 h-px bg-gray-200" />
                                    <Button
                                      className="w-full bg-transparent px-4 py-4 text-gray-800 shadow-none border-none rounded-none flex justify-between items-center h-auto"
                                      onClick={() => {
                                        setProfileSheetOpen(false)
                                        router.push(AGILUS_COINS_PAGE)
                                      }}
                                    >
                                      <div className="flex items-center gap-3">
                                        <AgilusCoinsIcon />
                                        <span className="text-body-sm font-medium">
                                          DDRC Agilus Coins
                                        </span>
                                      </div>
                                      <ChevronRight
                                        size={20}
                                        className="text-gray-400"
                                      />
                                    </Button>
                                  </div>
                                </div>

                                {/* Logout */}
                                <div className="px-4 mt-5 mb-6">
                                  <Button
                                    className="w-full bg-white px-4 py-4 text-gray-800 shadow-none border-none rounded-xl flex justify-start items-center gap-3 h-auto"
                                    onClick={() => {
                                      setProfileSheetOpen(false)
                                      handleUserLogout()
                                    }}
                                  >
                                    <LogOut
                                      size={20}
                                      className="text-gray-800"
                                    />
                                    <span className="text-body-sm font-medium">
                                      Logout
                                    </span>
                                  </Button>
                                </div>
                              </SheetContent>
                            </Sheet>
                          </>
                        ) : (
                          // Sign In Button - only show for logged-out users after mount
                          <ActionButton
                            variant="primary"
                            title="Sign in"
                            className="min-w-[80px]"
                            onClick={() => {
                              dispatch(
                                updateLoginData({
                                  ...loginState,
                                  activeStep: 1,
                                  hasOTPSent: false,
                                  loginModalIsOpen: true,
                                })
                              )
                            }}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {/* Row 2 (mobile only): Location + Search bar */}
              {showCityOrSearchbar && (
                <div className="lg:hidden">
                  <MWebLocationAndSearchBar
                    className={'searchbar_navbar'}
                    locationModalIsOpen={locationModalIsOpen}
                    setLocationModalIsOpen={setLocationModalIsOpen}
                    isMounted={isMounted}
                    isCityListFetched={isCityListFetched}
                    showSearchBar={showSearchBar}
                  />
                </div>
              )}
            </div>
          </header>

          <MobileDrawer
            open={navIsOpen}
            onOpenChange={() => setNavIsOpen(!navIsOpen)}
          />
          <SecondaryNav sharedCatalog={sharedCatalog} />
        </Fragment>
      )}
    </Fragment>
  )
}

export default Navbar
